export enum AnalyticsDataProperty {
  action = 'action',
  clickArea = 'click_area',
  clickPath = 'click_path',
  contentLanguage = 'content_language',
  event = 'event',
  mediaType = 'media_type',
  mediaUrl = 'media_url',
  pageScreenName = 'page_screen_name',
  pageScreenType = 'page_screen_type',
  pageScreenTypeSeo = 'page_screen_type_seo',
  phrasebookPageNumber = 'phrasebook_page_number',
  phraseSelected = 'phrase_selected',
  searchQuery = 'search_query',
  searchResults = 'search_results',
  sourceLanguage = 'source_language',
  targetLanguage = 'target_language',
  videoAction = 'video_action',
  videoName = 'video_name',
  videoTime = 'video_time',
}

export enum AnalyticsDataAction {
  signUp = 'sign_up',
  upgradeToPro = 'upgrade_to_pro',
}

export enum AnalyticsDataEvent {
  clickAccordion = 'click_accordion',
  clickAppStore = 'click_app_store',
  clickCta = 'click_cta',
  clickSocial = 'click_social',
  learnableSnippet = 'learnable_snippet',
  phrasebookPhraseSelected = 'phrasebook_phrase_selected',
  phrasebookSearch = 'phrasebook_search',
  videoAction = 'video_action',
  viewContent = 'view_content',
}

export enum AnalyticsDataClickArea {
  content = 'Content',
  footer = 'Footer',
  header = 'Header',
}

export enum AnalyticsDataClickPathAppStore {
  appleAppStore = 'Apple App Store',
  googlePlayStore = 'Google Play Store',
}

export enum AnalyticsDataClickPathSocialMedia {
  facebook = 'Facebook',
  instagram = 'Instagram',
  twitter = 'Twitter',
  youtube = 'Youtube',
}

export enum AnalyticsDataPageScreenType {
  officialCourse = 'Official course',
  topic = 'Topic',
  scenario = 'Scenario',
  immersiveScenario = 'Immersive scenario',
  learnImmerseCommunicateScenario = 'Learn immerse communicate scenario',
  campaignRootPage = 'Campaign - Root-page',
  campaignSubPage = 'Campaign - Sub-page',
  phrasebook = 'Phrasebook',
  learnable = 'Learnable',
  tag = 'Tag',
  errorPage = 'Error page',
}

export enum AnalyticsDataVideoAction {
  start = 'start',
  watched50Percent = '50_percent',
}

export enum AnalyticsDataMediaType {
  audio = 'audio',
  video = 'video',
}

export type CommonAnalyticsData = {
  [AnalyticsDataProperty.pageScreenTypeSeo]: 'true';
  [AnalyticsDataProperty.pageScreenType]: AnalyticsDataPageScreenType;
  [AnalyticsDataProperty.pageScreenName]: string;
  [AnalyticsDataProperty.contentLanguage]: string;
  [AnalyticsDataProperty.sourceLanguage]: string;
  [AnalyticsDataProperty.targetLanguage]: string | null;
};

export type AnalyticsEventClickAccordion = CommonAnalyticsData & {
  [AnalyticsDataProperty.event]: AnalyticsDataEvent.clickAccordion;
  [AnalyticsDataProperty.clickArea]: AnalyticsDataClickArea;
  [AnalyticsDataProperty.clickPath]: string;
};

export type AnalyticsEventClickAppStore = CommonAnalyticsData & {
  [AnalyticsDataProperty.event]: AnalyticsDataEvent.clickAppStore;
  [AnalyticsDataProperty.clickArea]: AnalyticsDataClickArea;
  [AnalyticsDataProperty.clickPath]: AnalyticsDataClickPathAppStore;
};

export type AnalyticsEventClickCta = CommonAnalyticsData & {
  [AnalyticsDataProperty.event]: AnalyticsDataEvent.clickCta;
  [AnalyticsDataProperty.action]: AnalyticsDataAction;
  [AnalyticsDataProperty.clickArea]: AnalyticsDataClickArea;
  [AnalyticsDataProperty.clickPath]: string;
};

export type AnalyticsEventClickSocial = CommonAnalyticsData & {
  [AnalyticsDataProperty.event]: AnalyticsDataEvent.clickSocial;
  [AnalyticsDataProperty.clickArea]: AnalyticsDataClickArea;
  [AnalyticsDataProperty.clickPath]: AnalyticsDataClickPathSocialMedia;
};

export type AnalyticsEventLearnableSnippet = CommonAnalyticsData & {
  [AnalyticsDataProperty.event]: AnalyticsDataEvent.learnableSnippet;
  [AnalyticsDataProperty.mediaType]: AnalyticsDataMediaType;
  [AnalyticsDataProperty.mediaUrl]: string;
  /**
   * The `video_` prefix is semantically incorrect if the media type is `audio`. However, we're
   * forced to use it for consistency, as these properties are used in other similar events.
   */
  [AnalyticsDataProperty.videoAction]: AnalyticsDataVideoAction.start;
  [AnalyticsDataProperty.videoName]: string;
};

export type AnalyticsEventPhrasebookSearch = CommonAnalyticsData & {
  [AnalyticsDataProperty.event]: AnalyticsDataEvent.phrasebookSearch;
  [AnalyticsDataProperty.searchQuery]: string;
  [AnalyticsDataProperty.searchResults]: number;
};

export type AnalyticsEventPhrasebookPhraseSelected = CommonAnalyticsData & {
  [AnalyticsDataProperty.event]: AnalyticsDataEvent.phrasebookPhraseSelected;
  [AnalyticsDataProperty.searchQuery]: string;
  [AnalyticsDataProperty.searchResults]: number;
  [AnalyticsDataProperty.phraseSelected]: string;
  [AnalyticsDataProperty.phrasebookPageNumber]: number;
};

export type AnalyticsEventVideoAction = CommonAnalyticsData & {
  [AnalyticsDataProperty.event]: AnalyticsDataEvent.videoAction;
  [AnalyticsDataProperty.videoAction]: AnalyticsDataVideoAction;
  [AnalyticsDataProperty.videoName]: string;
  [AnalyticsDataProperty.videoTime]: number; // in seconds
};

export type AnalyticsEventViewContent = CommonAnalyticsData & {
  [AnalyticsDataProperty.event]: AnalyticsDataEvent.viewContent;
};

export type AnalyticsEvent =
  | AnalyticsEventClickAccordion
  | AnalyticsEventClickAppStore
  | AnalyticsEventClickCta
  | AnalyticsEventClickSocial
  | AnalyticsEventLearnableSnippet
  | AnalyticsEventPhrasebookPhraseSelected
  | AnalyticsEventPhrasebookSearch
  | AnalyticsEventVideoAction
  | AnalyticsEventViewContent;
